import { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Stars } from '../components/Stars';
import { useTenant } from '../components/TenantContext';
import { findShop } from '../lib/tenants';
import { fmtDate, fmtRelative } from '../lib/format';
import { translateText } from '../lib/api';

type Review = {
  id: string;
  customer_name: string;
  rating: number;
  comment: string;
  status: 'pending' | 'needs_review' | 'published' | 'hidden';
  language: string | null;
  shop_brand: string | null;
  country: string | null;
  created_at: string;
  product?: {
    id: string;
    name: string | null;
    article_no: string | null;
    brand: string | null;
  } | null;
};

type StatusTab = 'pending' | 'needs_review' | 'published' | 'hidden' | 'all';

const TABS: ReadonlyArray<{ key: StatusTab; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'needs_review', label: 'Needs review' },
  { key: 'published', label: 'Published' },
  { key: 'hidden', label: 'Hidden' },
  { key: 'all', label: 'All' },
];

const LIST = gql`
  query Reviews($where: reviews_bool_exp!, $limit: Int!) {
    reviews(where: $where, limit: $limit, order_by: { created_at: desc }) {
      id customer_name rating comment status language shop_brand country created_at
      product { id name article_no brand }
    }
    counts: reviews_aggregate { aggregate { count } }
    pending: reviews_aggregate(where: { status: { _eq: pending } }) { aggregate { count } }
    needs_review: reviews_aggregate(where: { status: { _eq: needs_review } }) { aggregate { count } }
    published: reviews_aggregate(where: { status: { _eq: published } }) { aggregate { count } }
    hidden: reviews_aggregate(where: { status: { _eq: hidden } }) { aggregate { count } }
  }
`;

const SET_STATUS = gql`
  mutation SetStatus($id: uuid!, $status: review_status!, $publishedAt: timestamptz) {
    update_reviews_by_pk(
      pk_columns: { id: $id }
      _set: { status: $status, published_at: $publishedAt }
    ) { id status }
  }
`;

const REJECT_REASONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'spam', label: 'Spam / Werbung' },
  { value: 'off_topic', label: 'Off-Topic' },
  { value: 'abuse', label: 'Beleidigung / Hass' },
  { value: 'duplicate', label: 'Duplikat' },
  { value: 'pii', label: 'Persönliche Daten' },
  { value: 'other', label: 'Anderer Grund' },
];

export function ReviewsPage() {
  const { selected: selectedShops } = useTenant();
  const [tab, setTab] = useState<StatusTab>('pending');
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState<number[]>([]);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; undo?: () => void } | null>(null);

  const where = useMemo(() => {
    const w: Record<string, unknown> = {};
    if (tab !== 'all') w.status = { _eq: tab };
    if (selectedShops.length > 0) w.country = { _in: selectedShops };
    if (ratingFilter.length > 0) w.rating = { _in: ratingFilter };
    if (search.trim()) {
      const term = `%${search.trim()}%`;
      w._or = [
        { comment: { _ilike: term } },
        { customer_name: { _ilike: term } },
        { product: { name: { _ilike: term } } },
      ];
    }
    return w;
  }, [tab, selectedShops, ratingFilter, search]);

  const { data, loading, refetch } = useQuery(LIST, {
    variables: { where, limit: 200 },
    fetchPolicy: 'cache-and-network',
  });
  const [setStatus] = useMutation(SET_STATUS);

  const reviews: Review[] = data?.reviews ?? [];

  useEffect(() => {
    setSelection(new Set());
  }, [tab, selectedShops, ratingFilter, search]);

  function toggleSelect(id: string) {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (selection.size === reviews.length) setSelection(new Set());
    else setSelection(new Set(reviews.map((r) => r.id)));
  }

  async function applyStatus(id: string, status: 'published' | 'hidden') {
    const prev = reviews.find((r) => r.id === id)?.status;
    await setStatus({
      variables: {
        id,
        status,
        publishedAt: status === 'published' ? new Date().toISOString() : null,
      },
    });
    setToast({
      msg: status === 'published' ? 'Review veröffentlicht.' : 'Review verborgen.',
      undo: prev
        ? async () => {
            await setStatus({
              variables: { id, status: prev, publishedAt: null },
            });
            refetch();
            setToast(null);
          }
        : undefined,
    });
    refetch();
  }

  async function bulkApply(status: 'published' | 'hidden') {
    const ids = Array.from(selection);
    await Promise.all(
      ids.map((id) =>
        setStatus({
          variables: {
            id,
            status,
            publishedAt: status === 'published' ? new Date().toISOString() : null,
          },
        }),
      ),
    );
    setToast({ msg: `${ids.length} Reviews aktualisiert.` });
    setSelection(new Set());
    refetch();
  }

  const counts = {
    pending: data?.pending?.aggregate?.count ?? 0,
    needs_review: data?.needs_review?.aggregate?.count ?? 0,
    published: data?.published?.aggregate?.count ?? 0,
    hidden: data?.hidden?.aggregate?.count ?? 0,
    all: data?.counts?.aggregate?.count ?? 0,
  } satisfies Record<StatusTab, number>;

  const openReview = openId ? reviews.find((r) => r.id === openId) ?? null : null;

  return (
    <div className="page">
      <div className="inbox-head">
        <div className="inbox-head-row">
          <div>
            <h2 className="inbox-title">Reviews</h2>
            <div className="inbox-sub">
              {selectedShops.length === 0
                ? 'Alle Shops · Moderation, Übersetzung, Antworten.'
                : `${selectedShops.length} Shop${selectedShops.length === 1 ? '' : 's'} · ${selectedShops
                    .map((c) => findShop(c)?.flag ?? '')
                    .join(' ')}`}
            </div>
          </div>
          <span className="stat-pill">
            <span className="material-symbols-rounded" style={{ fontSize: 16, color: 'var(--brand-orange)' }}>
              schedule
            </span>
            <b>{counts.pending}</b> ausstehend
          </span>
        </div>

        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`tab${tab === t.key ? ' on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <span className="count">{counts[t.key]}</span>
            </button>
          ))}
        </div>

        <div className="filterbar">
          <span className="search">
            <span className="material-symbols-rounded">search</span>
            <input
              type="search"
              placeholder="Reviews durchsuchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </span>
          <span className="seg" role="group" aria-label="Sterne-Filter">
            {[5, 4, 3, 2, 1].map((n) => (
              <button
                key={n}
                type="button"
                className={ratingFilter.includes(n) ? 'on' : ''}
                onClick={() =>
                  setRatingFilter((r) =>
                    r.includes(n) ? r.filter((x) => x !== n) : [...r, n],
                  )
                }
              >
                {n}★
              </button>
            ))}
          </span>
          {(ratingFilter.length > 0 || search) && (
            <button
              type="button"
              className="chip"
              onClick={() => {
                setRatingFilter([]);
                setSearch('');
              }}
            >
              <span className="material-symbols-rounded">close</span>
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {selection.size > 0 && (
        <div className="selbar">
          <span>
            <b>{selection.size}</b> ausgewählt
          </span>
          <button type="button" className="bulk-approve" onClick={() => bulkApply('published')}>
            ✓ Approve {selection.size}
          </button>
          <button type="button" onClick={() => bulkApply('hidden')}>
            ✕ Hide {selection.size}
          </button>
          <button type="button" onClick={() => setSelection(new Set())}>
            Auswahl aufheben
          </button>
        </div>
      )}

      <div className="list-wrap">
        <div className="list-head">
          <div>
            <span
              className={`row-checkbox${selection.size === reviews.length && reviews.length > 0 ? ' on' : ''}`}
              onClick={selectAll}
              role="checkbox"
              aria-checked={selection.size === reviews.length && reviews.length > 0}
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <div>Reviewer</div>
          <div>Rating</div>
          <div>Review</div>
          <div>Produkt</div>
          <div>Status</div>
          <div>Datum</div>
          <div style={{ textAlign: 'right' }}>Aktion</div>
        </div>
        {loading && reviews.length === 0 && <div className="loader">Reviews werden geladen…</div>}
        {!loading && reviews.length === 0 && (
          <div className="empty">Keine Reviews gefunden, die zu den Filtern passen.</div>
        )}
        {reviews.map((r) => (
          <ReviewRow
            key={r.id}
            review={r}
            selected={selection.has(r.id)}
            onToggle={() => toggleSelect(r.id)}
            onOpen={() => setOpenId(r.id)}
            onApprove={() => applyStatus(r.id, 'published')}
            onReject={() => applyStatus(r.id, 'hidden')}
          />
        ))}
      </div>

      {openReview && (
        <ReviewDrawer
          review={openReview}
          onClose={() => setOpenId(null)}
          onApply={(status) => applyStatus(openReview.id, status)}
        />
      )}

      {toast && (
        <div className="toast" role="status">
          <span>{toast.msg}</span>
          {toast.undo && (
            <button type="button" onClick={toast.undo}>
              Rückgängig
            </button>
          )}
          <button type="button" onClick={() => setToast(null)} aria-label="Schließen">
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

function ReviewRow({
  review,
  selected,
  onToggle,
  onOpen,
  onApprove,
  onReject,
}: {
  review: Review;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const country = review.country ?? guessCountry(review.language);
  const shop = country ? findShop(country) : undefined;
  const lang = review.language?.toUpperCase() ?? 'DE';
  const isFinal = review.status === 'published' || review.status === 'hidden';

  return (
    <div className={`row${selected ? ' selected' : ''}`} onClick={onOpen} role="button">
      <div onClick={(e) => e.stopPropagation()}>
        <span
          className={`row-checkbox${selected ? ' on' : ''}`}
          onClick={onToggle}
          role="checkbox"
          aria-checked={selected}
          tabIndex={0}
          style={{ cursor: 'pointer' }}
        />
      </div>
      <div>
        <div className="reviewer-name">
          {review.customer_name || 'Anonym'}
        </div>
        <div className="reviewer-meta">
          {shop && <span className="flag">{shop.flag}</span>}
          <span>{lang}</span>
        </div>
      </div>
      <div>
        <Stars value={review.rating} />
      </div>
      <div className="row-text" title={review.comment}>
        {(review.comment ?? '').slice(0, 80)}
        {review.comment && review.comment.length > 80 ? '…' : ''}
      </div>
      <div className="row-product">
        {review.product?.name ?? '—'}
        {review.product?.article_no && <span className="sku">{review.product.article_no}</span>}
      </div>
      <div>
        <span className={`badge ${review.status}`}>{statusLabel(review.status)}</span>
      </div>
      <div className="date">{fmtDate(review.created_at)}</div>
      <div className="row-actions" onClick={(e) => e.stopPropagation()}>
        {isFinal ? (
          <span className={`status-icon ${review.status}`}>
            <span className="material-symbols-rounded">
              {review.status === 'published' ? 'check_circle' : 'cancel'}
            </span>
          </span>
        ) : (
          <>
            <button type="button" className="approve" onClick={onApprove} aria-label="Approve">
              <span className="material-symbols-rounded">check</span>
            </button>
            <button type="button" className="reject" onClick={onReject} aria-label="Reject">
              <span className="material-symbols-rounded">close</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ReviewDrawer({
  review,
  onClose,
  onApply,
}: {
  review: Review;
  onClose: () => void;
  onApply: (status: 'published' | 'hidden') => void;
}) {
  const [showRejectMenu, setShowRejectMenu] = useState(false);
  const [reply, setReply] = useState('');
  const [translation, setTranslation] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateErr, setTranslateErr] = useState<string | null>(null);
  const country = review.country ?? guessCountry(review.language);
  const shop = country ? findShop(country) : undefined;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Reset translation when the user opens a different review.
  useEffect(() => {
    setTranslation(null);
    setTranslateErr(null);
    setTranslating(false);
  }, [review.id]);

  const isFinal = review.status === 'published' || review.status === 'hidden';
  const sourceLang = (review.language ?? 'de').toLowerCase();
  const targetLang: 'de' | 'en' = sourceLang === 'de' ? 'en' : 'de';

  async function handleTranslate() {
    if (!review.comment) return;
    if (translation != null) {
      setTranslation(null);
      return;
    }
    setTranslating(true);
    setTranslateErr(null);
    try {
      const out = await translateText(review.comment, targetLang, sourceLang);
      setTranslation(out.translated);
    } catch (e) {
      setTranslateErr((e as Error).message);
    } finally {
      setTranslating(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <aside className="drawer" role="dialog" aria-label="Review-Details">
        <header className="drawer-head">
          <h2>Review</h2>
          <span className={`badge ${review.status}`}>{statusLabel(review.status)}</span>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Schließen">
            <span className="material-symbols-rounded">close</span>
          </button>
        </header>
        <div className="drawer-body">
          <div className="d-section d-reviewer">
            <div className="d-label">Reviewer</div>
            <div className="name">
              {review.customer_name || 'Anonym'}
              {shop && <span className="flag" aria-hidden="true">{shop.flag}</span>}
            </div>
            <div className="meta">
              {(review.language ?? 'de').toUpperCase()} · {country ?? '—'} · {fmtRelative(review.created_at)}
            </div>
          </div>

          <div className="d-section">
            <div className="d-label">Bewertung</div>
            <Stars value={review.rating} size="lg" />
          </div>

          <div className="d-section">
            <div className="d-label">Reviewtext</div>
            <div className="d-text">
              {review.comment || <em style={{ color: 'var(--fg-3)' }}>Kein Text hinterlassen</em>}
            </div>
            {translation && (
              <div className="d-text-translated">
                <div className="d-label" style={{ marginBottom: 4, color: 'var(--brand-cyan)' }}>
                  {targetLang.toUpperCase()} · Übersetzung
                </div>
                {translation}
              </div>
            )}
            {review.comment && (
              <button
                type="button"
                className="d-translate-btn"
                onClick={handleTranslate}
                disabled={translating}
              >
                <span className={`material-symbols-rounded${translating ? ' spin' : ''}`}>
                  {translating ? 'progress_activity' : 'translate'}
                </span>
                {translating
                  ? 'Übersetzung läuft …'
                  : translation != null
                    ? 'Original anzeigen'
                    : `Auf ${targetLang.toUpperCase()} übersetzen`}
              </button>
            )}
            {translateErr && (
              <div style={{ color: 'var(--brand-red)', font: '500 12px/16px var(--font-body)', marginTop: 6 }}>
                {translateErr}
              </div>
            )}
          </div>

          {review.product && (
            <div className="d-section">
              <div className="d-label">Produkt</div>
              <div className="d-product">
                <div>
                  <div>{review.product.name}</div>
                  <div className="sku">{review.product.article_no ?? '—'}</div>
                </div>
                <div className="sku">{review.product.brand ?? ''}</div>
              </div>
            </div>
          )}

          <div className="d-section">
            <div className="d-label">Bestellung</div>
            <div className="d-order">
              <div>
                <div>Verifizierter Kauf</div>
                <div className="meta">Shop: {review.shop_brand ?? '—'}</div>
              </div>
              <div className="meta">{fmtDate(review.created_at)}</div>
            </div>
          </div>

          <div className="d-section">
            <div className="d-label">Antwort</div>
            <div className="reply-box">
              <textarea
                className="reply-textarea"
                placeholder="Antwort an den Kunden …"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <div className="reply-foot">
                <span className="d-label" style={{ margin: 0 }}>
                  {reply.length} Zeichen
                </span>
                <button
                  type="button"
                  className="reply-send"
                  onClick={() => {
                    setReply('');
                    alert('Antwort als Vorlage gespeichert (Mock — echtes Senden folgt mit Provider-Anbindung).');
                  }}
                  disabled={!reply.trim()}
                >
                  Senden
                </button>
              </div>
            </div>
          </div>
        </div>

        <footer className="drawer-foot">
          {!isFinal && (
            <>
              <button type="button" className="btn primary" onClick={() => onApply('published')}>
                <span className="material-symbols-rounded">check</span>
                Approve
              </button>
              <div style={{ position: 'relative' }}>
                <button type="button" className="btn danger" onClick={() => setShowRejectMenu((s) => !s)}>
                  <span className="material-symbols-rounded">close</span>
                  Reject
                </button>
                {showRejectMenu && (
                  <div className="reject-menu">
                    {REJECT_REASONS.map((r) => (
                      <button
                        type="button"
                        key={r.value}
                        className="reject-option"
                        onClick={() => {
                          onApply('hidden');
                          setShowRejectMenu(false);
                        }}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {isFinal && (
            <button type="button" className="btn" onClick={() => onApply(review.status === 'published' ? 'hidden' : 'published')}>
              Status wechseln
            </button>
          )}
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>
            Schließen
          </button>
        </footer>
      </aside>
    </>
  );
}

function statusLabel(s: string): string {
  switch (s) {
    case 'pending': return 'Pending';
    case 'needs_review': return 'Needs review';
    case 'published': return 'Published';
    case 'hidden': return 'Hidden';
    default: return s;
  }
}

function guessCountry(lang: string | null | undefined): string | null {
  switch ((lang ?? '').toLowerCase()) {
    case 'de': return 'DE';
    case 'it': return 'IT';
    case 'fr': return 'FR';
    case 'en': return 'GB';
    default: return null;
  }
}
